import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { initSentry } from "./lib/sentry.ts";
import "./index.css";
import App from "./App.tsx";

// Initialize Sentry before rendering
initSentry();

// Safety net: ship every mutation error to Sentry + a fallback toast ONLY if
// neither the hook nor the call site declared their own onError. The flag
// mutation.state.submittedAt+options path isn't a perfect proxy for per-call
// overrides, so we err on the side of silent-when-already-handled: if the
// hook declares onError, we skip the toast here. Per-call onError still
// fires independently; the worst case is a single missed toast, which
// Sentry will still capture.
const mutationCache = new MutationCache({
  onError: (error, _variables, _context, mutation) => {
    Sentry.captureException(error, {
      tags: { source: "react-query-mutation" },
      extra: { mutationKey: mutation.options.mutationKey },
    });
    if (mutation.options.onError) return;
    const msg =
      (error as { response?: { data?: { message?: string } }; message?: string })
        ?.response?.data?.message ||
      (error as Error)?.message ||
      "Something went wrong";
    toast.error(msg);
  },
});

// Create React Query client
const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div style={{
          minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
          flexDirection: "column", gap: 16, padding: 32, fontFamily: "sans-serif", background: "#f9fafb",
        }}>
          <div style={{ fontSize: 48 }}>⚠️</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#111827", margin: 0 }}>Something went wrong</h1>
          <p style={{ color: "#6b7280", margin: 0, textAlign: "center", maxWidth: 480 }}>
            An unexpected error occurred. The issue has been reported to Sentry.
          </p>
          {error instanceof Error && (
            <pre style={{
              background: "#fee2e2", color: "#991b1b", borderRadius: 8, padding: "12px 16px",
              fontSize: 12, maxWidth: 600, whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0,
            }}>
              {error.message}
            </pre>
          )}
          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <button
              onClick={resetError}
              style={{
                background: "#2563eb", color: "#fff", border: "none", borderRadius: 8,
                padding: "10px 20px", fontWeight: 600, cursor: "pointer", fontSize: 14,
              }}
            >
              Try again
            </button>
            <button
              onClick={() => window.location.href = "/"}
              style={{
                background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 8,
                padding: "10px 20px", fontWeight: 600, cursor: "pointer", fontSize: 14,
              }}
            >
              Go to home
            </button>
          </div>
        </div>
      )}
    >
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
